import React from 'react';
import { Link } from 'react-router-dom';
import AdBanner from '../components/AdBanner';
import SEO from '../components/SEO';
import './LandingPage.css';

function LandingPage() {
  const tools = [
    {
      id: 'countdown-timer',
      title: 'Countdown Timer',
      description: 'Set up countdown timers with customizable alarms. Perfect for productivity, cooking, or any time-sensitive tasks.',
      icon: '⏱️',
      link: '/countdown-timer',
      available: true
    },
    {
      id: 'calculator',
      title: 'Calculator',
      description: 'Advanced calculator with scientific functions, history, and more.',
      icon: '🧮',
      link: '/calculator',
      available: true
    },
    {
      id: 'unit-converter',
      title: 'Unit Converter',
      description: 'Convert between different units of measurement including length, weight, temperature, and more.',
      icon: '📏',
      link: '/unit-converter',
      available: true
    },
    {
      id: 'currency-converter',
      title: 'Currency Converter',
      description: 'Convert between 200+ currencies with live exchange rates including major world currencies and cryptocurrencies.',
      icon: '💱',
      link: '/currency-converter',
      available: true
    },
    {
      id: 'qr-generator',
      title: 'QR Code Generator',
      description: 'Generate QR codes for URLs, text, contact information, and more.',
      icon: '📱',
      link: '/qr-generator',
      available: true
    },
    {
      id: 'color-picker',
      title: 'Color Picker',
      description: 'Pick colors, generate palettes, and convert between different color formats.',
      icon: '🎨',
      link: '/color-picker',
      available: true
    },
    {
      id: 'password-generator',
      title: 'Password Generator',
      description: 'Generate secure passwords with customizable criteria.',
      icon: '🔐',
      link: '/password-generator',
      available: true
    },
    {
      id: 'link-shortener',
      title: 'Link Shortener',
      description: 'Shorten long URLs with optional custom short codes. Redirects to your original link.',
      icon: '🔗',
      link: '/link-shortener',
      available: true
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Infi Tools",
    "description": "Free online tools collection including calculator, unit converter, password generator, QR code generator, color picker, countdown timer, and currency converter.",
    "url": "https://infi-tools.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://infi-tools.com/?q={search_term_string}",
      "query-input": "required name=search_term_string"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Infi Tools",
      "logo": {
        "@type": "ImageObject",
        "url": "https://infi-tools.com/Infi_Tool_logo_with_text.png"
      }
    },
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": [
        {
          "@type": "SoftwareApplication",
          "name": "Online Calculator",
          "description": "Advanced calculator with scientific functions and history",
          "url": "https://infi-tools.com/calculator"
        },
        {
          "@type": "SoftwareApplication", 
          "name": "Unit Converter",
          "description": "Convert between different units of measurement",
          "url": "https://infi-tools.com/unit-converter"
        },
        {
          "@type": "SoftwareApplication",
          "name": "Password Generator", 
          "description": "Generate secure passwords with customizable criteria",
          "url": "https://infi-tools.com/password-generator"
        },
        {
          "@type": "SoftwareApplication",
          "name": "QR Code Generator",
          "description": "Generate QR codes for URLs, text, and contact information",
          "url": "https://infi-tools.com/qr-generator"
        },
        {
          "@type": "SoftwareApplication",
          "name": "Color Picker",
          "description": "Pick colors and generate color palettes",
          "url": "https://infi-tools.com/color-picker"
        },
        {
          "@type": "SoftwareApplication",
          "name": "Currency Converter",
          "description": "Convert between 200+ currencies with live exchange rates",
          "url": "https://infi-tools.com/currency-converter"
        },
        {
          "@type": "SoftwareApplication",
          "name": "Countdown Timer",
          "description": "Set countdown timers with customizable alarms",
          "url": "https://infi-tools.com/countdown-timer"
        }
      ]
    }
  };

  return (
    <div className="landing-page">
      <SEO 
        title="Free Online Tools for Everyone"
        description="Free online tools collection including calculator, unit converter, password generator, QR code generator, color picker, countdown timer, and currency converter. Fast, secure, and easy to use."
        keywords="free online tools, calculator, unit converter, password generator, QR code generator, color picker, countdown timer, currency converter, web tools, utilities, free calculator, online calculator, currency exchange, color picker tool"
        canonicalUrl="https://infi-tools.com"
        structuredData={structuredData}
      />
      <div className="container">
        {/* Hero Section */}
        <section className="hero">
          <div className="hero-content">
            <h1 className="hero-title">
              Free Online Tools for Everyone
            </h1>
            <p className="hero-subtitle">
              Discover our collection of useful utilities designed to make your life easier. 
              All tools are free, fast, and work directly in your browser.
            </p>
          </div>
        </section>

        {/* Ad Banner */}
        <AdBanner adSlot="2336145372" />

        {/* Tools Grid */}
        <section className="tools-section">
          <h2 className="section-title">Available Tools</h2>
          <div className="tools-grid">
            {tools.map((tool) => (
              <div key={tool.id} className={`tool-card ${!tool.available ? 'tool-card--disabled' : ''}`}>
                <div className="tool-icon">{tool.icon}</div>
                <h3 className="tool-title">{tool.title}</h3>
                <p className="tool-description">{tool.description}</p>
                <div className="tool-footer">
                  {tool.available ? (
                    <Link to={tool.link} className="btn">
                      Try Now
                    </Link>
                  ) : (
                    <span className="coming-soon">Coming Soon</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Features Section */}
        <section className="features-section">
          <h2 className="section-title">Why Choose Infi Tools?</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🚀</div>
              <h3>Fast & Responsive</h3>
              <p>All tools are optimized for speed and work seamlessly on any device.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <h3>Privacy First</h3>
              <p>Your data stays in your browser. We don't collect or store your personal information.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💻</div>
              <h3>No Installation</h3>
              <p>Use all tools directly in your browser without downloading any software.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🆓</div>
              <h3>Completely Free</h3>
              <p>All tools are free to use with no hidden costs or subscription fees.</p>
            </div>
          </div>
        </section>

        {/* Bottom Ad Banner */}
        <AdBanner adSlot="1069281265" />
      </div>
    </div>
  );
}

export default LandingPage; 